import {useState} from 'react';
import {useApolloClient} from '@apollo/client';
import {Alert, Button, Select} from 'antd';
import DBModal from 'components/modal/modal';
import {GET_SUPPORT_PERFORMANCE} from './resolvers';
import {describeRange, overviewInput} from './performanceUtils';
import styles from './performance.module.scss';

const MAX_RECIPIENTS = 5;
const STAFF_EMAIL = /^[^\s@]+@(joindaba\.com|dabafinance\.com)$/i;

/** Why these recipients can't be sent, or null when they can. */
export const recipientsError = emails => {
  if (!emails.length) return 'Add at least one recipient.';
  if (emails.length > MAX_RECIPIENTS) {
    return `Up to ${MAX_RECIPIENTS} recipients.`;
  }
  const outside = emails.filter(email => !STAFF_EMAIL.test(email));
  if (outside.length) {
    return `Only @joindaba.com and @dabafinance.com addresses: ${outside.join(
      ', ',
    )}`;
  }
  return null;
};

/**
 * Emails the full filtered report as CSV. The backend builds the file from
 * every filtered aggregate — never from the rows on screen — so this only
 * sends the current filters and the recipients.
 *
 * Success here means the export was queued, not that the email arrived.
 */
export const ExportPerformanceModal = ({isOpen, onClose, filters}) => {
  const client = useApolloClient();
  const [emails, setEmails] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [queued, setQueued] = useState(false);

  const close = () => {
    setError(null);
    setQueued(false);
    onClose();
  };

  const send = async () => {
    const problem = recipientsError(emails);
    if (problem) {
      setError(problem);
      return;
    }
    setSending(true);
    setError(null);
    try {
      const {data} = await client.query({
        query: GET_SUPPORT_PERFORMANCE,
        variables: {
          input: {...overviewInput(filters), export: true, emails},
        },
        fetchPolicy: 'no-cache',
      });
      const result = data?.getSupportPerformance;
      if (result?.__typename === 'Error') {
        setError(result.message);
      } else if (result?.data?.exportRequested) {
        setQueued(true);
      } else {
        setError("The export wasn't queued. Try again.");
      }
    } catch (requestError) {
      setError(requestError.message || "Couldn't request the export.");
    }
    setSending(false);
  };

  return (
    <DBModal
      isOpen={isOpen}
      handleClose={close}
      width={520}
      content={
        <div className={styles.exportModal}>
          <h2>Email performance CSV</h2>
          <p className={styles.exportScope}>
            Every agent, Unattributed and the total for{' '}
            {describeRange(filters.from, filters.to)}, with the filters
            currently applied.
          </p>
          {queued ? (
            <Alert
              type="success"
              showIcon
              message="Export requested; the CSV will be emailed when ready."
            />
          ) : (
            <>
              <Select
                mode="tags"
                className={styles.exportEmails}
                placeholder="name@joindaba.com"
                value={emails}
                tokenSeparators={[',', ' ', ';']}
                onChange={values => {
                  setEmails(values.map(value => value.trim()).filter(Boolean));
                  setError(null);
                }}
                open={false}
              />
              <p className={styles.exportHint}>
                Up to 5 recipients, @joindaba.com or @dabafinance.com.
              </p>
              {error && (
                <Alert
                  type="error"
                  showIcon
                  message={error}
                  className={styles.exportError}
                />
              )}
            </>
          )}
          <div className={styles.exportActions}>
            <Button onClick={close}>{queued ? 'Done' : 'Cancel'}</Button>
            {!queued && (
              <Button
                type="primary"
                loading={sending}
                disabled={sending}
                onClick={send}>
                Send CSV
              </Button>
            )}
          </div>
        </div>
      }
    />
  );
};
